/**
 * Autofill functionality for the Advanced Retirement Calculator
 * 
 * This module handles mapping Moneytor products to retirement calculator inputs.
 * Uses the canonical calculateBalances function for accurate currency conversion.
 * 
 * @module domain/retirement/autofill
 */

import type { Fiat, FrontendCryptoFormat, FrontendStockFormat } from "~/types/global.types";
import type { PensionAsset } from "~/types/assets.types";
import { Forms } from "~/utils/products";
import { calculateBalances } from "~/calculations/portfolioCalculations/calculateBalances";

import {
  AssetCategory,
  WithdrawalMode,
  DEFAULT_RETURN_ASSUMPTIONS,
  ASSUMED_PENSION_INVESTMENT_YEARLY_RETURN,
  RETIREMENT_AGE_MALE,
  RETIREMENT_AGE_FEMALE,
  LifeEventKind,
  LifeEventFrequency,
  LifeEventDirection,
  EndYearMode,
} from "./constants";
import type {
  AdvancedRetirementInputs,
  AutofillInput,
  AutofillResult,
  LoanEvent,
  DebtPaymentSummary,
  LifeEvent,
  PartnerInputs,
  PensionBreakdown,
  PensionEarlyKitzbaStream,
  PortfolioByCategory,
  ProductBreakdown,
  RetirementFamilyMember,
} from "./types";
import { calculateAge, generateId, convertToBaseCurrency } from "./utils";
import { isExcludedPensionType } from "./productMappers";
import { calculateLoanMonthlyPayment, calculateLoanTotalRepayment } from "./lifeEvents";
import {
  buildProductPlan,
  isMarriedStatus,
  isKitzbaCapable,
  parseGender,
  PensionWithdrawalType,
  PENSION_EARLY_SELL_TAG,
} from "./pensionLaw";
import type { PensionProductPlan } from "./pensionLaw";

// ============================================================================
// HELPER: Get original value from product based on form type
// ============================================================================

function getOriginalValue(product: Record<string, any>, form: string): number {
  switch (form) {
    case Forms.BANK:
    case "bank2":
      return product.amount || 0;
    case Forms.SHARE:
      // Sum of stock values
      if (product.stocksData && Array.isArray(product.stocksData)) {
        return product.stocksData.reduce((sum: number, stock: any) => {
          return sum + (stock.amount || 0) * (stock.stockPrice || 0);
        }, product.cash || 0);
      }
      return product.cash || 0;
    case Forms.RSU:
      // Sum of vested RSU values
      if (product.rsuData && Array.isArray(product.rsuData)) {
        const stockPrice = product.stockPrice || 0;
        return product.rsuData.reduce((sum: number, grant: any) => {
          return sum + (grant.vestedAmount || 0) * stockPrice;
        }, 0);
      }
      return 0;
    case Forms.CRYPTO:
      // Sum of crypto coin values (in USD typically)
      if (product.cryptoData && Array.isArray(product.cryptoData)) {
        return product.cryptoData.reduce((sum: number, coin: any) => {
          return sum + (coin.amount || 0) * (coin.coinRate || 0);
        }, product.cash || 0);
      }
      return product.cash || 0;
    case Forms.PENSION:
      return product.amount || 0;
    case Forms.REALESTATE:
      return product.value || 0;
    case Forms.OTHER:
      return product.currentValue || 0;
    default:
      return 0;
  }
}

// ============================================================================
// HELPER: Get original currency from product
// ============================================================================

function getOriginalCurrency(product: Record<string, any>): string {
  if (product.currency?.value) {
    return product.currency.value;
  }
  // Default to ILS if no currency specified
  return "ILS";
}

// ============================================================================
// PENSION CALCULATION
// ============================================================================

/**
 * Calculate expected pension value at retirement for a single pension product
 * Uses compound growth with monthly deposits and management fees
 * 
 * @param currentAmount - Current pension balance
 * @param yearsToRetirement - Number of years until retirement
 * @param monthlyDeposit - Total monthly deposit (employee + employer)
 * @param managementFeeFromDeposit - Fee percentage on deposits (e.g., 1 for 1%)
 * @param managementFeeFromSavings - Fee percentage on accumulated savings (e.g., 0.13 for 0.13%)
 * @param yearlyReturnPercent - Expected yearly return percentage (e.g., 4.31 for 4.31%)
 * @returns Expected value at retirement
 */
function calculatePensionEndValue(
  currentAmount: number,
  yearsToRetirement: number,
  monthlyDeposit: number,
  managementFeeFromDeposit: number,
  managementFeeFromSavings: number,
  yearlyReturnPercent: number = ASSUMED_PENSION_INVESTMENT_YEARLY_RETURN
): number {
  let value = currentAmount;
  
  for (let i = 0; i < yearsToRetirement; i++) {
    // Apply yearly return
    value = value * (1 + yearlyReturnPercent / 100);
    // Add yearly deposits minus deposit fee
    value += monthlyDeposit * 12 * (1 - managementFeeFromDeposit / 100);
    // Subtract management fee from savings
    value = value * (1 - managementFeeFromSavings / 100);
  }
  
  return value;
}

/**
 * Calculate total expected pension at retirement from all pension products
 * Excludes קרנות השתלמות (Keren Hishtalmut) as it's not a retirement pension
 * 
 * @param pensionProducts - Array of pension products
 * @param yearsToRetirement - Years until retirement
 * @param fiatData - Currency data for conversion
 * @returns Object with total expected pension and breakdown
 */
export function calculateExpectedPensionAtRetirement(
  pensionProducts: (PensionAsset & { id?: number | string })[],
  yearsToRetirement: number,
  fiatData: Fiat[]
): { 
  totalExpectedPension: number; 
  includedProducts: { id?: number | string; name: string; currentValue: number; expectedValue: number; productType?: string }[];
  excludedProducts: { id?: number | string; name: string; currentValue: number; reason: string }[];
} {
  const includedProducts: { id?: number | string; name: string; currentValue: number; expectedValue: number; productType?: string }[] = [];
  const excludedProducts: { id?: number | string; name: string; currentValue: number; reason: string }[] = [];
  let totalExpectedPension = 0;

  for (const pension of pensionProducts) {
    const currentValueInBase = convertToBaseCurrency(
      pension.amount || 0,
      pension.currency,
      fiatData
    );

    // Skip קרנות השתלמות - these are categorized as OTHER (immediately available)
    // not PENSION (locked until 67). They're excluded from pension projection calculation.
    if (isExcludedPensionType(pension)) {
      excludedProducts.push({
        id: (pension as any).id,
        name: pension.name,
        currentValue: currentValueInBase,
        reason: `${pension.productType?.value} - נזיל ומוצג בקטגוריית "אחר"`,
      });
      continue;
    }

    // Use product's years to retirement if available, otherwise use calculated value
    const productYearsToRetirement = pension.yearsToRetirement ?? yearsToRetirement;
    
    // Get monthly deposits
    const monthlyDeposit = (pension.monthlyDepositEmployee || 0) + (pension.monthlyDepositEmployer || 0);
    
    // Get management fees (default to conservative values if not available)
    const depositFee = pension.managementFeeFromDeposit ?? 1; // Default 1%
    const savingsFee = pension.managementFeeFromSavings ?? 0.5; // Default 0.5%
    
    // Calculate expected value at retirement
    const expectedValue = calculatePensionEndValue(
      currentValueInBase,
      productYearsToRetirement,
      monthlyDeposit,
      depositFee,
      savingsFee
    );

    includedProducts.push({
      id: (pension as any).id,
      name: pension.name,
      currentValue: currentValueInBase,
      expectedValue,
      productType: pension.productType?.value,
    });

    totalExpectedPension += expectedValue;
  }

  return {
    totalExpectedPension,
    includedProducts,
    excludedProducts,
  };
}

// ============================================================================
// HELPER: Map form type to asset category
// ============================================================================

function formToCategory(form: string): AssetCategory | null {
  switch (form) {
    case Forms.BANK:
    case "bank2":
      return AssetCategory.CASH;
    case Forms.SHARE:
      return AssetCategory.STOCKS;
    case Forms.RSU:
      return AssetCategory.RSU;
    case Forms.CRYPTO:
      return AssetCategory.CRYPTO;
    case Forms.PENSION:
      return AssetCategory.PENSION;
    case Forms.REALESTATE:
      return AssetCategory.REALESTATE;
    case Forms.OTHER:
      return AssetCategory.OTHER;
    default:
      return null;
  }
}

// ============================================================================
// HELPER: Create RSU unvested life events
// ============================================================================

function createRSUUnvestedEvents(product: Record<string, any>, stockPriceInBase: number): LifeEvent[] {
  const unvestedEvents: LifeEvent[] = [];
  const rsuData = product.rsuData;
  
  if (!rsuData || !Array.isArray(rsuData)) return unvestedEvents;
  
  for (const grant of rsuData) {
    const vestedAmount = grant.vestedAmount || 0;
    const totalAmount = grant.totalAmount || 0;
    const unvestedAmount = totalAmount - vestedAmount;
    
    if (unvestedAmount > 0 && grant.grantDate) {
      const grantDate = new Date(grant.grantDate);
      const currentYear = new Date().getFullYear();
      // Assume 4-year vesting with annual cliff (common)
      const yearsRemaining = Math.max(0, 4 - (currentYear - grantDate.getFullYear()));
      
      if (yearsRemaining > 0) {
        const annualVestAmount = unvestedAmount / yearsRemaining;
        for (let i = 1; i <= yearsRemaining; i++) {
          unvestedEvents.push({
            kind: LifeEventKind.CASHFLOW,
            id: generateId(),
            name: `RSU Vesting: ${product.name}`,
            startYear: currentYear + i,
            frequency: LifeEventFrequency.ONE_TIME,
            amount: annualVestAmount * stockPriceInBase,
            direction: LifeEventDirection.INFLOW,
            categoryTag: "RSU Vesting",
            isAutoGenerated: true,
            sourceProductId: product.id,
            enabled: true,
            notes: `Estimated vesting from ${product.name}`,
          });
        }
      }
    }
  }
  
  return unvestedEvents;
}

// ============================================================================
// HELPER: Create loan/debt life events
// ============================================================================

/**
 * Calculate remaining months for a loan route based on its start date and total period.
 *
 * @param routeStartDate - Route-level start date (may be empty/undefined)
 * @param productStartDate - Product-level start date (fallback)
 * @param debtPeriodInMonths - Total loan period in months
 * @param remainder - Remaining loan balance (fallback estimation)
 * @param monthlyRepayment - Monthly repayment amount (fallback estimation)
 * @returns Number of remaining months (0 if already ended or unknown)
 */
function calculateLoanRemainingMonths(
  routeStartDate: string | undefined,
  productStartDate: string | undefined,
  debtPeriodInMonths: number | undefined,
  remainder: number | undefined,
  monthlyRepayment: number | undefined
): number {
  const startDateStr = routeStartDate || productStartDate;

  if (startDateStr && debtPeriodInMonths && debtPeriodInMonths > 0) {
    const start = new Date(startDateStr);
    // Guard against invalid dates
    if (!isNaN(start.getTime())) {
      const now = new Date();
      const monthsElapsed =
        (now.getFullYear() - start.getFullYear()) * 12 +
        (now.getMonth() - start.getMonth());
      return Math.max(0, debtPeriodInMonths - monthsElapsed);
    }
  }

  // Fallback: estimate from remainder and monthly payment
  if (remainder && remainder > 0 && monthlyRepayment && monthlyRepayment > 0) {
    return Math.ceil(remainder / monthlyRepayment);
  }

  return 0;
}

/**
 * Create LOAN life events for debt products.
 *
 * Each loan route with remaining months generates a LoanEvent that runs
 * from now until the loan is fully repaid. These events are marked with
 * `includedInExpenses: true` because the user's cashflow-derived monthlySpend
 * already contains the loan payments. The projection engine:
 *  - Does NOT add them as outflows while active (avoids double-counting).
 *  - Applies a spendingReduction after the loan ends so base expenses drop.
 *
 * @param product - The debt product (frontend-transformed format)
 * @param fiatData - Currency data for conversion
 * @returns Array of LoanEvent life events
 */
function createDebtLifeEvents(
  product: Record<string, any>,
  fiatData: Fiat[]
): LifeEvent[] {
  const events: LifeEvent[] = [];
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const ownership = product.ownership ?? 100;

  // Get the routes (try transformed format then raw format)
  const routes: any[] =
    product.routesData ??
    product.additional_data?.assetsData ??
    [];

  if (!Array.isArray(routes) || routes.length === 0) return events;

  // Product-level start date fallback
  const productStartDate =
    product.startDate ||
    product.start_date ||
    product.additional_data?.start_date;

  for (const route of routes) {
    // Normalize field names (transformed vs raw format)
    const routeStartDate = route.startDate || route.start_date;
    const debtPeriodInMonths = route.debtPeriodInMonths || route.debt_period_in_months;
    const monthlyRepayment = route.monthlyRepayment || route.monthly_repayment;
    const remainder = route.remainder;
    const interest = route.interest || 0;

    const remainingMonths = calculateLoanRemainingMonths(
      routeStartDate,
      productStartDate,
      debtPeriodInMonths,
      remainder,
      monthlyRepayment
    );

    // Skip if loan has already ended or no repayment info
    if (remainingMonths <= 0) continue;

    // Get the remaining balance in base currency
    let remainderInBase = remainder || 0;
    const baseCurrency = fiatData.find((f) => f.value === "ILS");
    const productCurrency = fiatData.find(
      (f) => f.value === product.currency?.value
    );
    if (
      baseCurrency &&
      productCurrency &&
      productCurrency.rate &&
      baseCurrency.rate
    ) {
      remainderInBase =
        remainderInBase * (baseCurrency.rate / productCurrency.rate);
    }

    // Apply ownership percentage
    remainderInBase = remainderInBase * (ownership / 100);

    // Skip negligible amounts
    if (remainderInBase < 1) continue;

    // Calculate the calendar year when the loan ends
    const totalMonthsFromNow = currentMonth + remainingMonths;
    const loanEndYear =
      currentYear + Math.floor(totalMonthsFromNow / 12);

    // Only create event if the loan ends in the future (at least next year)
    if (loanEndYear <= currentYear) continue;

    // Route suffix for multi-route loans (e.g., mortgage with multiple tracks)
    const routeSuffix = routes.length > 1
      ? ` (מסלול ${routes.indexOf(route) + 1})`
      : "";

    // Distinguish between mortgage and regular loan
    const isMortgage = product.debtType === "mortgage";
    const eventName = isMortgage
      ? `החזר משכנתא: ${product.name}${routeSuffix}`
      : `החזר הלוואה: ${product.name}${routeSuffix}`;

    // Calculate the monthly payment and total repayment from loan params
    const monthlyPayment = calculateLoanMonthlyPayment(remainderInBase, interest, remainingMonths);
    const totalRepayment = calculateLoanTotalRepayment(remainderInBase, interest, remainingMonths);

    const event: LoanEvent = {
      kind: LifeEventKind.LOAN,
      id: generateId(),
      name: eventName,
      startYear: currentYear,
      endYear: loanEndYear,
      endYearMode: EndYearMode.SPECIFIC_YEAR,
      frequency: LifeEventFrequency.MONTHLY,
      amount: Math.round(monthlyPayment),
      loanAmount: Math.round(remainderInBase),
      interestRate: interest,
      periodMonths: remainingMonths,
      totalRepayment: Math.round(totalRepayment),
      enabled: true,
      // Mark as included in expenses — the user's cashflow-derived monthlySpend
      // already contains these payments, so the projection engine should NOT add
      // them again as outflows. When the loan ends the engine will apply a
      // spendingReduction to lower the base expenses.
      includedInExpenses: true,
      icon: isMortgage ? "home" : "bank",
      categoryTag: "debt_auto",
      isAutoGenerated: true,
      sourceProductId: product.id,
      notes: isMortgage
        ? `אירוע אוטומטי: החזר משכנתא חודשי (${remainingMonths} חודשים נותרו, עד ${loanEndYear}). החזר חודשי: ₪${Math.round(monthlyPayment).toLocaleString()}`
        : `אירוע אוטומטי: החזר הלוואה חודשי (${remainingMonths} חודשים נותרו, עד ${loanEndYear}). החזר חודשי: ₪${Math.round(monthlyPayment).toLocaleString()}`,
    };

    events.push(event);
  }

  return events;
}

// ============================================================================
// PENSION OWNER ATTRIBUTION (couple support)
// ============================================================================

/** Stable per-person key for a pension product (teudat zehut, else owner name). */
function getPensionOwnerKey(product: Record<string, any>): string | undefined {
  const idNumber = product.idNumber || product.additional_data?.id_number;
  if (idNumber) return String(idNumber).trim();
  const owner = product.accountOwner || product.additional_data?.account_owner;
  if (owner) return `name:${String(owner).trim()}`;
  return undefined;
}

function getPensionOwnerName(product: Record<string, any>): string | undefined {
  const owner = product.accountOwner || product.additional_data?.account_owner;
  return owner ? String(owner).trim() : undefined;
}

function getPensionOwnerGender(product: Record<string, any>): "male" | "female" | undefined {
  const g = product.gender || product.additional_data?.min;
  return g ? parseGender(g) : undefined;
}

function getPensionOwnerAge(product: Record<string, any>): number | undefined {
  const dob = product.taarichLeyda || product.additional_data?.taarich_leyda;
  if (dob) {
    const age = calculateAge(dob);
    if (!Number.isNaN(age) && age > 0) return age;
  }
  return undefined;
}

function getPensionOwnerMarital(product: Record<string, any>): string | undefined {
  return product.matsavMishpachti || product.additional_data?.matsav_mishpachti;
}

/** Stable identity key for a product used to match plans back to products. */
function productKeyOf(p: Record<string, any>): string {
  return String(p.id ?? p.name);
}

export interface PensionOwnerProfile {
  key: string;
  name?: string;
  gender: "male" | "female";
  age?: number;
  maritalStatus?: string;
  productKeys: string[];
  totalCurrentValue: number;
}

/**
 * Group pension products by owner identity (id_number, else account_owner).
 * Missing demographic fields are back-filled from any product in the group.
 */
function buildPensionOwnerProfiles(
  pensionProducts: (PensionAsset & { id?: number | string })[],
  fallbackGender: "male" | "female",
): PensionOwnerProfile[] {
  const groups = new Map<string, PensionOwnerProfile>();
  for (const p of pensionProducts) {
    const key = getPensionOwnerKey(p) ?? "__unknown__";
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        name: getPensionOwnerName(p),
        gender: getPensionOwnerGender(p) ?? fallbackGender,
        age: getPensionOwnerAge(p),
        maritalStatus: getPensionOwnerMarital(p),
        productKeys: [],
        totalCurrentValue: 0,
      };
      groups.set(key, g);
    }
    if (g.age == null) g.age = getPensionOwnerAge(p);
    if (!g.maritalStatus) g.maritalStatus = getPensionOwnerMarital(p);
    if (!g.name) g.name = getPensionOwnerName(p);
    g.productKeys.push(productKeyOf(p));
    g.totalCurrentValue += p.amount || 0;
  }
  return Array.from(groups.values());
}

/**
 * Resolve which owner group is the primary partner (matching the resolved
 * user demographics) and which is the second partner. Only two partners are
 * modelled; any additional owners are attributed to the primary.
 */
export function resolvePrimaryAndPartner(
  groups: PensionOwnerProfile[],
  primary: { gender: "male" | "female"; age: number },
): { primary?: PensionOwnerProfile; partner?: PensionOwnerProfile } {
  if (groups.length === 0) return {};
  if (groups.length === 1) return { primary: groups[0] };

  // Primary = the group closest in age to the resolved user age (prefer same gender).
  const sorted = [...groups].sort((a, b) => {
    const da = Math.abs((a.age ?? primary.age) - primary.age);
    const db = Math.abs((b.age ?? primary.age) - primary.age);
    if (da !== db) return da - db;
    const ga = a.gender === primary.gender ? 0 : 1;
    const gb = b.gender === primary.gender ? 0 : 1;
    if (ga !== gb) return ga - gb;
    return b.totalCurrentValue - a.totalCurrentValue;
  });
  const primaryGroup = sorted[0];
  const rest = sorted.slice(1).sort((a, b) => b.totalCurrentValue - a.totalCurrentValue);
  return { primary: primaryGroup, partner: rest[0] };
}

// ----------------------------------------------------------------------------
// Family-based attribution (preferred when family members are known)
// ----------------------------------------------------------------------------

/** Full display name for a family member (name, else email), else undefined. */
function familyMemberName(member?: RetirementFamilyMember): string | undefined {
  if (!member) return undefined;
  const name = [member.firstName, member.lastName]
    .filter((part) => part != null && String(part).trim().length > 0)
    .join(" ")
    .trim();
  if (name) return name;
  const email = member.email ? String(member.email).trim() : "";
  return email || undefined;
}

/** The owning Moneytor account of a product, if resolved from user_products. */
function getProductUserId(product: Record<string, any>): string | undefined {
  const uid = product.userId ?? product.user_id;
  return uid != null && String(uid).trim() !== "" ? String(uid).trim() : undefined;
}

/**
 * Group pension products by their owning Moneytor account (`user_id`), falling
 * back to matching the Masleka `id_number` against a known family member, and
 * finally to the Masleka owner key. Names come from the family member when
 * available so the UI shows real partner names instead of "בן/בת זוג 2".
 */
function buildPensionOwnerProfilesByUser(
  pensionProducts: (PensionAsset & { id?: number | string })[],
  fallbackGender: "male" | "female",
  membersById: Map<string, RetirementFamilyMember>,
): PensionOwnerProfile[] {
  const membersByIdNumber = new Map<string, RetirementFamilyMember>();
  for (const member of membersById.values()) {
    if (member.idNumber) membersByIdNumber.set(String(member.idNumber).trim(), member);
  }

  const resolveKey = (p: Record<string, any>): string => {
    const uid = getProductUserId(p);
    if (uid) return uid;
    const idNumber = p.idNumber || p.additional_data?.id_number;
    const matchedMember = idNumber
      ? membersByIdNumber.get(String(idNumber).trim())
      : undefined;
    if (matchedMember) return matchedMember.userId;
    return getPensionOwnerKey(p) ?? "__unknown__";
  };

  const groups = new Map<string, PensionOwnerProfile>();
  for (const p of pensionProducts) {
    const key = resolveKey(p);
    const member = membersById.get(key);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        name: familyMemberName(member) ?? getPensionOwnerName(p),
        gender: getPensionOwnerGender(p) ?? fallbackGender,
        age: getPensionOwnerAge(p),
        maritalStatus: getPensionOwnerMarital(p),
        productKeys: [],
        totalCurrentValue: 0,
      };
      groups.set(key, g);
    }
    if (g.age == null) g.age = getPensionOwnerAge(p);
    if (!g.maritalStatus) g.maritalStatus = getPensionOwnerMarital(p);
    if (!g.name) g.name = familyMemberName(member) ?? getPensionOwnerName(p);
    g.productKeys.push(productKeyOf(p));
    g.totalCurrentValue += p.amount || 0;
  }
  return Array.from(groups.values());
}

/**
 * Resolve primary vs partner using the logged-in user as the primary. The
 * partner is the other family member (preferring a real member group over
 * unmatched Masleka groups, then by total value).
 */
function resolvePrimaryAndPartnerByUser(
  groups: PensionOwnerProfile[],
  currentUserId: string,
  membersById: Map<string, RetirementFamilyMember>,
): { primary?: PensionOwnerProfile; partner?: PensionOwnerProfile } {
  let primary = groups.find((g) => g.key === currentUserId);
  const others = groups.filter((g) => g.key !== currentUserId);
  const memberOthers = others.filter((g) => membersById.has(g.key));
  const pool = memberOthers.length > 0 ? memberOthers : others;
  const partner = [...pool].sort((a, b) => b.totalCurrentValue - a.totalCurrentValue)[0];

  // Synthesize a primary profile from member info when the current user has no
  // pension products, so their name still shows in couple mode.
  if (!primary) {
    const member = membersById.get(currentUserId);
    if (member) {
      primary = {
        key: currentUserId,
        name: familyMemberName(member),
        gender: "male",
        age: undefined,
        maritalStatus: undefined,
        productKeys: [],
        totalCurrentValue: 0,
      };
    }
  }
  return { primary, partner };
}

interface OwnerAttribution {
  belongsToPartner: boolean;
  gender: "male" | "female";
  isMarried: boolean;
  eligibilityAge: number;
  ownerId?: string;
  ownerName?: string;
}

/**
 * Build a per-product owner lookup for pension plan construction. Products
 * belonging to the resolved partner group are tagged `belongsToPartner`.
 */
function buildOwnerLookup(
  pensionProducts: (PensionAsset & { id?: number | string })[],
  primary: { gender: "male" | "female"; age: number; maritalStatus?: string; eligibilityAge: number; ownerKey?: string; ownerName?: string },
  partner: PensionOwnerProfile | undefined,
): (productKey: string) => OwnerAttribution {
  const partnerKeys = new Set(partner?.productKeys ?? []);
  const partnerGender = partner?.gender ?? "male";
  const partnerEligibilityAge = partnerGender === "female" ? RETIREMENT_AGE_FEMALE : RETIREMENT_AGE_MALE;
  const partnerMarried = isMarriedStatus(partner?.maritalStatus ?? primary.maritalStatus);
  const primaryMarried = isMarriedStatus(primary.maritalStatus);

  return (productKey: string): OwnerAttribution => {
    if (partner && partnerKeys.has(productKey)) {
      return {
        belongsToPartner: true,
        gender: partnerGender,
        isMarried: partnerMarried,
        eligibilityAge: partnerEligibilityAge,
        ownerId: partner.key,
        ownerName: partner.name,
      };
    }
    return {
      belongsToPartner: false,
      gender: primary.gender,
      isMarried: primaryMarried,
      eligibilityAge: primary.eligibilityAge,
      ownerId: primary.ownerKey,
      ownerName: primary.ownerName,
    };
  };
}

// ============================================================================
// MAIN AUTOFILL FUNCTION
// ============================================================================

/**
 * Build autofill inputs from Moneytor data
 * Uses the canonical calculateBalances function for accurate currency conversion.
 * 
 * @param input - Moneytor data including products, currencies, cashflow
 * @returns AutofillResult with inputs and detailed breakdown
 */
export function buildAutofillFromMoneytor(input: AutofillInput): AutofillResult {
  const { allProducts, fiatData, cryptoMarketData, stockMarketData, cashflow, averageSpend, averageIncome, userProfile } = input;
  
  const productBreakdowns: ProductBreakdown[] = [];
  const skippedProducts: { productId: number | string; reason: string }[] = [];
  const lifeEvents: LifeEvent[] = [];

  // Initialize portfolio by category
  const portfolioByCategory: PortfolioByCategory = {
    [AssetCategory.CASH]: 0,
    [AssetCategory.DEPOSIT]: 0,
    [AssetCategory.STOCKS]: 0,
    [AssetCategory.CRYPTO]: 0,
    [AssetCategory.RSU]: 0,
    [AssetCategory.PENSION]: 0,
    [AssetCategory.REALESTATE]: 0,
    [AssetCategory.OTHER]: 0,
    [AssetCategory.DEBT]: 0,
  };

  // Initialize debt summary
  const debtSummary: DebtPaymentSummary = {
    totalOutstandingDebt: 0,
    totalMonthlyPayments: 0,
    debts: [],
  };

  let liquidTotal = 0;
  let nonLiquidTotal = 0;

  // Use the canonical calculateBalances function for accurate currency conversion
  // This function properly handles crypto rates in USD, stock prices, and all currency conversions
  const { balancesInBase } = calculateBalances(
    allProducts,
    fiatData as Fiat[],
    (cryptoMarketData || []) as FrontendCryptoFormat[],
    (stockMarketData || []) as FrontendStockFormat[],
    { allForms: true, liquid: true }
  );

  // Process each product using the calculated balances
  for (const product of allProducts) {
    if (product.isFamilyJointDuplicate) continue;
    const { id, form } = product;
    const category = formToCategory(form);
    
    if (!category) {
      if (form !== Forms.DEBT) {
        skippedProducts.push({ productId: id, reason: `Unknown form: ${form}` });
      }
      // Handle debt separately
      if (form === Forms.DEBT) {
        const balance = Math.abs(balancesInBase[id] || 0);
        debtSummary.totalOutstandingDebt += balance;
        
        // Calculate monthly payment from routesData
        let monthlyPayment = 0;
        if (product.routesData && Array.isArray(product.routesData)) {
          for (const route of product.routesData) {
            if (route.monthlyRepayment) {
              monthlyPayment += route.monthlyRepayment;
            } else if (route.remainder && route.debtPeriodInMonths && route.debtPeriodInMonths > 1) {
              monthlyPayment += route.remainder / route.debtPeriodInMonths;
            }
          }
        }
        
        // Convert monthly payment to ILS if needed
        const baseCurrency = fiatData.find(f => f.value === "ILS");
        const productCurrency = fiatData.find(f => f.value === product.currency?.value);
        if (baseCurrency && productCurrency && productCurrency.rate && baseCurrency.rate) {
          monthlyPayment = monthlyPayment * (baseCurrency.rate / productCurrency.rate);
        }
        
        const ownershipRatio = (product.ownership ?? 100) / 100;
        debtSummary.totalMonthlyPayments += monthlyPayment * ownershipRatio;
        debtSummary.debts.push({
          remainder: balance,
          monthlyPayment: monthlyPayment * ownershipRatio,
          productId: id,
          productName: product.name,
          debtType: product.debtType,
        });

        // Add debt as a ProductBreakdown so it shows in "פירוט לפי מוצר"
        // Value is negative to represent a liability
        const debtBreakdown: ProductBreakdown = {
          productId: id,
          productName: product.name,
          form: Forms.DEBT,
          category: AssetCategory.DEBT,
          originalValue: -balance,
          originalCurrency: product.currency?.value || "ILS",
          valueInBaseCurrency: -balance,
          ownership: product.ownership ?? 100,
          isLiquid: false,
          includedInPortfolio: true,
          notes: product.debtType === "mortgage"
            ? `משכנתא · החזר חודשי: ₪${Math.round(monthlyPayment * ownershipRatio).toLocaleString()}`
            : `הלוואה · החזר חודשי: ₪${Math.round(monthlyPayment * ownershipRatio).toLocaleString()}`,
        };
        productBreakdowns.push(debtBreakdown);

        // Create LOAN life events for visibility and for modelling when loans end.
        // Events are marked `includedInExpenses: true` so the projection engine
        // does NOT double-count them as outflows (they're already in monthlySpend).
        // When the loan ends, the engine applies a spendingReduction.
        const debtEvents = createDebtLifeEvents(product, fiatData as Fiat[]);
        lifeEvents.push(...debtEvents);
      }
      continue;
    }

    const valueInBase = balancesInBase[id] || 0;
    const isLiquid = product.liquidity !== false && product.liquidity !== undefined ? product.liquidity : true;
    const ownership = product.ownership ?? 100;
    
    // For pension, check if it's קרן השתלמות (Keren Hishtalmut)
    // קרן השתלמות is NOT locked until age 67 - it's a savings fund that can be accessed
    // after ~6 years of vesting. We categorize it as OTHER (immediately available)
    // rather than PENSION (locked until retirement).
    const isKerenHistalmut = form === Forms.PENSION && isExcludedPensionType(product as PensionAsset);
    
    // For bank products, distinguish between checking accounts (CASH) and savings/deposits (DEPOSIT)
    const isBankDeposit = (form === Forms.BANK || form === "bank2") && 
      product.accountType?.value === "saving";
    
    // Determine the effective category:
    // - Regular pension -> PENSION (locked until 67)
    // - קרן השתלמות -> OTHER (immediately available, similar to savings)
    // - Bank savings/deposits -> DEPOSIT (earns interest)
    // - Bank checking/foreign -> CASH (0% return)
    let effectiveCategory = isKerenHistalmut ? AssetCategory.OTHER : category;
    if (isBankDeposit) {
      effectiveCategory = AssetCategory.DEPOSIT;
    }
    
    // Create product breakdown
    const breakdown: ProductBreakdown = {
      productId: id,
      productName: product.name,
      form,
      category: effectiveCategory,
      originalValue: getOriginalValue(product, form),
      originalCurrency: getOriginalCurrency(product),
      valueInBaseCurrency: valueInBase,
      ownership,
      isLiquid: form === Forms.PENSION ? product.liquidity === true : isLiquid,
      includedInPortfolio: true, // All products are included, just in different categories
      notes: isKerenHistalmut ? "קרן השתלמות - נזיל" : undefined,
    };
    
    productBreakdowns.push(breakdown);
    
    // Add to portfolio totals using the effective category
    portfolioByCategory[effectiveCategory] += valueInBase;
    
    if (breakdown.isLiquid) {
      liquidTotal += valueInBase;
    } else {
      nonLiquidTotal += valueInBase;
    }
    
    // Handle RSU unvested events
    if (form === Forms.RSU) {
      const stockPrice = product.stockPriceInBaseCurrency || product.stockPrice || 0;
      const unvestedEvents = createRSUUnvestedEvents(product, stockPrice);
      lifeEvents.push(...unvestedEvents);
    }
  }

  // Calculate totals
  const totalAssets = liquidTotal + nonLiquidTotal;
  const totalDebts = debtSummary.totalOutstandingDebt;
  const netWorth = totalAssets - totalDebts;

  // ---------------------------------------------------------------------------
  // Extract DOB, gender, and marital status.
  // Prefer userProfile, but fall back to masleka pension product data when the
  // profile is incomplete.
  // ---------------------------------------------------------------------------
  let currentAge = 30;
  let gender: "male" | "female" = userProfile?.gender ?? "male";
  let maritalStatus: string | undefined;

  if (userProfile?.age) {
    currentAge = userProfile.age;
  } else if (userProfile?.birthDate) {
    currentAge = calculateAge(userProfile.birthDate);
  } else {
    // Try to get DOB from masleka pension product additional_data
    const maslekaProduct = allProducts.find(
      (p) => p.form === Forms.PENSION && (p.taarichLeyda || p.additional_data?.taarich_leyda),
    );
    if (maslekaProduct) {
      const dob = maslekaProduct.taarichLeyda || maslekaProduct.additional_data?.taarich_leyda;
      if (dob) currentAge = calculateAge(dob);
    }
  }

  // Extract gender/marital from masleka if not already set from userProfile
  if (!userProfile?.gender) {
    const maslekaWithGender = allProducts.find(
      (p) => p.form === Forms.PENSION && (p.gender || p.additional_data?.min),
    );
    if (maslekaWithGender) {
      gender = parseGender(maslekaWithGender.gender || maslekaWithGender.additional_data?.min);
    }
  }
  const maslekaWithMarital = allProducts.find(
    (p) => p.form === Forms.PENSION && (p.matsavMishpachti || p.additional_data?.matsav_mishpachti),
  );
  if (maslekaWithMarital) {
    maritalStatus = maslekaWithMarital.matsavMishpachti || maslekaWithMarital.additional_data?.matsav_mishpachti;
  }

  // Determine pension eligibility age based on gender (Israeli law)
  const pensionEligibilityAge = gender === "female" ? RETIREMENT_AGE_FEMALE : RETIREMENT_AGE_MALE;
  const yearsToPensionEligibility = Math.max(0, pensionEligibilityAge - currentAge);
  const retirementAge = pensionEligibilityAge;

  // ---------------------------------------------------------------------------
  // Calculate expected pension and build per-product pension plans
  // ---------------------------------------------------------------------------
  const pensionProducts = allProducts
    .filter((p): p is PensionAsset & { id: number | string; form: string } => p.form === Forms.PENSION)
    .map(p => p as PensionAsset);

  // Couple detection: attribute pension products to their owner and pick
  // primary/partner. When family members are known, group by the owning
  // Moneytor account (real names, primary = logged-in user); otherwise fall
  // back to the Masleka id_number / account_owner heuristic.
  const familyMembers = input.userMembers ?? [];
  const currentUserId = input.currentUserId
    ? String(input.currentUserId).trim()
    : undefined;
  const membersById = new Map<string, RetirementFamilyMember>(
    familyMembers.map((member) => [String(member.userId), member]),
  );
  const useFamilyAttribution =
    familyMembers.length >= 2 && !!currentUserId && membersById.has(currentUserId);

  const ownerProfiles = useFamilyAttribution
    ? buildPensionOwnerProfilesByUser(pensionProducts, gender, membersById)
    : buildPensionOwnerProfiles(pensionProducts, gender);
  const { primary: primaryOwner, partner: partnerOwner } = useFamilyAttribution
    ? resolvePrimaryAndPartnerByUser(ownerProfiles, currentUserId as string, membersById)
    : resolvePrimaryAndPartner(ownerProfiles, { gender, age: currentAge });
  const hasPartner = partnerOwner != null;
  const partnerGender = partnerOwner?.gender ?? "male";
  const partnerEligibilityAge = partnerGender === "female" ? RETIREMENT_AGE_FEMALE : RETIREMENT_AGE_MALE;

  const ownerLookup = buildOwnerLookup(
    pensionProducts,
    { gender, age: currentAge, maritalStatus, eligibilityAge: pensionEligibilityAge, ownerKey: primaryOwner?.key, ownerName: primaryOwner?.name },
    partnerOwner,
  );

  // General attribution for ALL products (any form) so non-pension holdings can
  // be filtered by partner. Only meaningful in family mode; otherwise everything
  // is attributed to the primary.
  const partnerMember = useFamilyAttribution && partnerOwner
    ? membersById.get(partnerOwner.key)
    : undefined;
  const partnerIdNumber = partnerMember?.idNumber
    ? String(partnerMember.idNumber).trim()
    : undefined;
  const attributeProduct = (
    product: Record<string, any>,
  ): { ownerId?: string; ownerName?: string; belongsToPartner: boolean } => {
    if (useFamilyAttribution && partnerOwner) {
      const uid = getProductUserId(product);
      let belongsToPartner = false;
      if (uid) {
        belongsToPartner = uid === partnerOwner.key;
      } else {
        const idNumber = product.idNumber || product.additional_data?.id_number;
        if (idNumber && partnerIdNumber && String(idNumber).trim() === partnerIdNumber) {
          belongsToPartner = true;
        }
      }
      return belongsToPartner
        ? { ownerId: partnerOwner.key, ownerName: partnerOwner.name, belongsToPartner: true }
        : { ownerId: primaryOwner?.key, ownerName: primaryOwner?.name, belongsToPartner: false };
    }
    return { ownerId: primaryOwner?.key, ownerName: primaryOwner?.name, belongsToPartner: false };
  };

  // Tag every product breakdown with its owner for partner filtering.
  for (const breakdown of productBreakdowns) {
    const original = allProducts.find(
      (p) => String(p.id) === String(breakdown.productId),
    );
    if (!original) continue;
    const attribution = attributeProduct(original);
    breakdown.ownerId = attribution.ownerId;
    breakdown.ownerName = attribution.ownerName;
    breakdown.belongsToPartner = attribution.belongsToPartner;
  }

  const expectedPensionResult = calculateExpectedPensionAtRetirement(
    pensionProducts,
    yearsToPensionEligibility,
    fiatData as Fiat[]
  );

  // Build per-product pension plans using the pension law engine, attributing
  // each product to its owner (primary or partner) so gender/mekadem/timing use
  // the right person's demographics.
  const perProductPlan: PensionProductPlan[] = [];
  let totalMonthlyPensionAtRetirement = 0;
  let totalLumpSumAtRetirement = 0;
  let totalLumpSumTax = 0;
  // Per-partner aggregates (primary = not-partner).
  let primaryMonthlyPensionContribution = 0;
  let partnerMonthlyPensionContribution = 0;
  let partnerMonthlyPensionAtRetirement = 0;
  let partnerLumpSumAtRetirement = 0;
  let partnerCurrentPensionValue = 0;
  const partnerPensionProductIds: (number | string)[] = [];

  for (const p of expectedPensionResult.includedProducts) {
    const original = p.id != null
      ? pensionProducts.find(pp => (pp as any).id === p.id)
      : pensionProducts.find(pp => pp.name === p.name);
    if (!original) continue;

    const owner = ownerLookup(productKeyOf(original as Record<string, any>));
    const plan = buildProductPlan(
      original as PensionAsset & { id?: number | string },
      p.expectedValue,
      owner.eligibilityAge,
      owner.gender,
      owner.isMarried,
      undefined,
      { ownerId: owner.ownerId, ownerName: owner.ownerName, belongsToPartner: owner.belongsToPartner },
    );
    perProductPlan.push(plan);
    totalMonthlyPensionAtRetirement += plan.monthlyPensionAtRetirement;
    totalLumpSumAtRetirement += plan.lumpSumAtRetirement;
    totalLumpSumTax += plan.lumpSumTax;
    if (owner.belongsToPartner) {
      partnerMonthlyPensionContribution += plan.monthlyContribution;
      partnerMonthlyPensionAtRetirement += plan.monthlyPensionAtRetirement;
      partnerLumpSumAtRetirement += plan.lumpSumAtRetirement;
      partnerCurrentPensionValue += p.currentValue;
      if (p.id != null) partnerPensionProductIds.push(p.id);
    } else {
      primaryMonthlyPensionContribution += plan.monthlyContribution;
    }
  }

  const partnerExpectedPension = perProductPlan
    .filter(pl => pl.belongsToPartner)
    .reduce((s, pl) => s + pl.projectedValueAtRetirement, 0);

  // Build pension breakdown for UI display
  const pensionBreakdown: PensionBreakdown = {
    currentPensionValue: portfolioByCategory[AssetCategory.PENSION],
    expectedPensionAtRetirement: expectedPensionResult.totalExpectedPension,
    yearsToPensionEligibility,
    pensionEligibilityAge,
    products: expectedPensionResult.includedProducts.map(p => {
      const original = p.id != null
        ? pensionProducts.find(pp => (pp as any).id === p.id)
        : pensionProducts.find(pp => pp.name === p.name);
      const owner = ownerLookup(String(p.id ?? p.name));
      return {
        id: p.id,
        name: p.name,
        currentValue: p.currentValue,
        expectedValue: p.expectedValue,
        productType: p.productType,
        monthlyContribution: (original?.monthlyDepositEmployee || 0) + (original?.monthlyDepositEmployer || 0),
        ownerId: owner.ownerId,
        ownerName: owner.ownerName,
        belongsToPartner: owner.belongsToPartner,
      };
    }),
    excludedProducts: expectedPensionResult.excludedProducts,
    perProductPlan,
    totalMonthlyPensionAtRetirement,
    totalLumpSumAtRetirement,
    totalLumpSumTax,
  };

  // ---------------------------------------------------------------------------
  // Monthly spend & income
  // ---------------------------------------------------------------------------
  let monthlySpend = averageSpend || 15000;
  if (cashflow?.monthlyExpenses) {
    monthlySpend = cashflow.monthlyExpenses;
  }

  let householdIncome = 20000;
  const effectiveIncome = averageIncome || cashflow?.monthlyIncome;
  if (effectiveIncome) {
    householdIncome = effectiveIncome;
  }
  const monthlySavings = Math.max(0, householdIncome - monthlySpend);

  // In couple mode we don't know the individual salaries, so default to a 50/50
  // split of the known household income. The user adjusts this in the UI.
  const monthlyIncome = hasPartner ? Math.round(householdIncome / 2) : householdIncome;
  const partnerIncome = hasPartner ? householdIncome - monthlyIncome : 0;

  // Aggregate pension aggregates net of the partner's share (primary-only) so the
  // projection engine can treat the two pension pools independently.
  const primaryExpectedPension = expectedPensionResult.totalExpectedPension - partnerExpectedPension;
  const primaryCashflow = totalMonthlyPensionAtRetirement - partnerMonthlyPensionAtRetirement;
  const primaryLumpSum = totalLumpSumAtRetirement - partnerLumpSumAtRetirement;

  const partner: PartnerInputs | undefined = hasPartner
    ? {
        currentAge: partnerOwner?.age ?? currentAge,
        retirementAge: partnerEligibilityAge,
        pensionEligibilityAge: partnerEligibilityAge,
        monthlyIncome: partnerIncome,
        gender: partnerGender,
        maritalStatus: partnerOwner?.maritalStatus ?? maritalStatus,
        name: partnerOwner?.name,
        ownerId: partnerOwner?.key,
        totalMonthlyPensionContribution:
          partnerMonthlyPensionContribution > 0 ? partnerMonthlyPensionContribution : undefined,
        pensionContributionRate:
          partnerMonthlyPensionContribution > 0 && partnerIncome > 0
            ? partnerMonthlyPensionContribution / partnerIncome
            : undefined,
        expectedPensionAtRetirement: partnerExpectedPension > 0 ? partnerExpectedPension : undefined,
        pensionCashflowAtRetirement:
          partnerMonthlyPensionAtRetirement > 0 ? partnerMonthlyPensionAtRetirement : undefined,
        pensionLumpSumAtRetirement: partnerLumpSumAtRetirement > 0 ? partnerLumpSumAtRetirement : undefined,
        currentPensionValue: partnerCurrentPensionValue > 0 ? partnerCurrentPensionValue : undefined,
        pensionProductIds: partnerPensionProductIds.length > 0 ? partnerPensionProductIds : undefined,
      }
    : undefined;

  // Build the inputs
  const inputs: AdvancedRetirementInputs = {
    currentAge,
    retirementAge,
    pensionEligibilityAge,
    lifeExpectancy: 90,
    monthlySpend,
    monthlyIncome,
    monthlySavings,
    withdrawalRate: 0.04,
    inflation: 0.02,
    taxRate: undefined,
    currency: "ILS",
    startPortfolioByCategory: portfolioByCategory,
    returnAssumptions: { ...DEFAULT_RETURN_ASSUMPTIONS },
    lifeEvents,
    withdrawalMode: WithdrawalMode.EXPENSE_BASED,
    includeNonLiquidRealestate: true,
    includeNonLiquidPension: false,
    includeUnvestedRSU: true,
    expectedPensionAtRetirement: primaryExpectedPension > 0 ? primaryExpectedPension : undefined,
    pensionCashflowAtRetirement: primaryCashflow > 0 ? primaryCashflow : undefined,
    pensionLumpSumAtRetirement: primaryLumpSum > 0 ? primaryLumpSum : undefined,
    perProductPensionPlan: perProductPlan.length > 0 ? perProductPlan : undefined,
    gender,
    maritalStatus,
    primaryName: primaryOwner?.name,
    primaryOwnerId: primaryOwner?.key,
    totalMonthlyPensionContribution: primaryMonthlyPensionContribution > 0 ? primaryMonthlyPensionContribution : undefined,
    pensionContributionRate: (primaryMonthlyPensionContribution > 0 && monthlyIncome > 0)
      ? primaryMonthlyPensionContribution / monthlyIncome
      : undefined,
    partner,
  };

  const spendableBeforeRetirement = totalAssets - portfolioByCategory[AssetCategory.PENSION];

  return {
    inputs,
    breakdown: {
      products: productBreakdowns,
      debtSummary,
      liquidTotal,
      nonLiquidTotal,
      totalAssets,
      totalDebts,
      netWorth,
      spendableBeforeRetirement,
      pensionBreakdown,
    },
    debug: {
      currencyConversions: [],
      skippedProducts,
      expectedPension: expectedPensionResult,
    },
  };
}

// ============================================================================
// RECOMPUTE PENSION FROM CURRENT INPUTS (single source of truth)
// ============================================================================

export interface RecomputePensionResult {
  perProductPensionPlan: PensionProductPlan[];
  pensionBreakdown: PensionBreakdown;
  /** Primary partner's expected pension (their products only) */
  expectedPensionAtRetirement: number;
  /** Primary partner's monthly kitzba (their products only) */
  pensionCashflowAtRetirement: number;
  /** Primary partner's lump sum (their products only) */
  pensionLumpSumAtRetirement: number;
  /** Primary partner's total monthly pension contribution */
  totalMonthlyPensionContribution: number;
  /** Primary partner's pension contribution rate (contribution / their salary) */
  pensionContributionRate: number | undefined;
  /** Early kitzba streams (annuities started before official pension age) */
  pensionEarlyKitzbaStreams: PensionEarlyKitzbaStream[];
  /** IDs of early-sell life events handled as early kitzba (should be filtered out before projection) */
  handledEarlySellEventIds: string[];
  /** Recomputed partner pension aggregates (only present in couple mode) */
  partner?: {
    expectedPensionAtRetirement?: number;
    pensionCashflowAtRetirement?: number;
    pensionLumpSumAtRetirement?: number;
    totalMonthlyPensionContribution?: number;
    pensionContributionRate?: number;
    currentPensionValue?: number;
    pensionProductIds?: (number | string)[];
  };
}

/**
 * Parse per-product early-withdrawal overrides (productId string -> withdraw age)
 * from life events tagged as pension early sells.
 */
function parseEarlyWithdrawalOverrides(
  inputs: AdvancedRetirementInputs,
): Record<string, { withdrawAtAge: number; eventId: string }> {
  const overrides: Record<string, { withdrawAtAge: number; eventId: string }> = {};
  if (!inputs.lifeEvents || inputs.currentAge == null) return overrides;
  const currentYear = new Date().getFullYear();
  const prefix = `${PENSION_EARLY_SELL_TAG}_`;
  for (const ev of inputs.lifeEvents) {
    if (
      ev.categoryTag === PENSION_EARLY_SELL_TAG &&
      ev.id?.startsWith(prefix) &&
      "startYear" in ev
    ) {
      const pid = ev.id.slice(prefix.length);
      const startYear = (ev as { startYear: number }).startYear;
      const withdrawAtAge = inputs.currentAge + (startYear - currentYear);
      overrides[pid] = { withdrawAtAge, eventId: ev.id };
    }
  }
  return overrides;
}

/**
 * Recomputes pension plan and breakdown from current inputs and product data.
 * Used so that when retirement age (or current age, gender) changes, graph and
 * PensionTab both consume data from the same calculation run.
 */
export function recomputePensionFromInputs(
  inputs: AdvancedRetirementInputs,
  pensionProducts: (PensionAsset & { id?: number | string })[],
  fiatData: Fiat[],
  gender: "male" | "female"
): RecomputePensionResult | null {
  if (pensionProducts.length === 0) {
    return null;
  }

  const currentAge = inputs.currentAge;
  const retirementAge = inputs.retirementAge;
  const officialRetirementAge = gender === "female" ? RETIREMENT_AGE_FEMALE : RETIREMENT_AGE_MALE;
  const pensionEligibilityAge = inputs.pensionEligibilityAge ?? officialRetirementAge;
  const yearsToPensionEligibility = Math.max(0, pensionEligibilityAge - currentAge);
  const isMarried = isMarriedStatus(inputs.maritalStatus);

  // Couple attribution: products listed on inputs.partner.pensionProductIds
  // belong to the partner and use the partner's gender/eligibility/marital.
  const partnerInput = inputs.partner;
  const hasPartner = partnerInput != null;
  const partnerProductKeys = new Set(
    (partnerInput?.pensionProductIds ?? []).map((id) => String(id)),
  );
  const partnerGender = partnerInput?.gender ?? "male";
  const partnerEligibilityAge =
    partnerInput?.pensionEligibilityAge ??
    (partnerGender === "female" ? RETIREMENT_AGE_FEMALE : RETIREMENT_AGE_MALE);
  const partnerRetirementAge = partnerInput?.retirementAge ?? partnerEligibilityAge;
  const partnerMarried = isMarriedStatus(partnerInput?.maritalStatus ?? inputs.maritalStatus);

  // Early kitzba only applies when the user plans to retire before the official
  // pension age. Otherwise the early-withdrawal toggle keeps its 35% lump-sum sale.
  const isEarlyRetirement = retirementAge < officialRetirementAge;
  const earlyWithdrawalOverrides = parseEarlyWithdrawalOverrides(inputs);

  const expectedPensionResult = calculateExpectedPensionAtRetirement(
    pensionProducts,
    yearsToPensionEligibility,
    fiatData
  );

  const perProductPlan: PensionProductPlan[] = [];
  const pensionEarlyKitzbaStreams: PensionEarlyKitzbaStream[] = [];
  const handledEarlySellEventIds: string[] = [];
  let totalMonthlyPensionAtRetirement = 0;
  let totalLumpSumAtRetirement = 0;
  let totalLumpSumTax = 0;
  // Per-partner aggregates.
  let primaryMonthlyPensionContribution = 0;
  let partnerMonthlyPensionContribution = 0;
  let partnerMonthlyPensionAtRetirement = 0;
  let partnerLumpSumAtRetirement = 0;
  let partnerExpectedPension = 0;
  let partnerCurrentPensionValue = 0;

  for (const p of expectedPensionResult.includedProducts) {
    const original =
      p.id != null
        ? pensionProducts.find((pp) => (pp as any).id === p.id)
        : pensionProducts.find((pp) => pp.name === p.name);
    if (!original) continue;

    const belongsToPartner =
      hasPartner && partnerProductKeys.has(productKeyOf(original as Record<string, any>));
    const ownerGender = belongsToPartner ? partnerGender : gender;
    const ownerMarried = belongsToPartner ? partnerMarried : isMarried;
    const ownerRetirementAge = belongsToPartner ? partnerRetirementAge : retirementAge;
    const ownerId = belongsToPartner ? partnerInput?.ownerId : inputs.primaryOwnerId;
    const ownerName = belongsToPartner ? partnerInput?.name : inputs.primaryName;
    const ownerInfo = { ownerId, ownerName, belongsToPartner };

    // Determine if this product has an active early kitzba (early retirement +
    // kitzba-capable + user toggled early withdrawal on it). Early kitzba only
    // applies to the primary partner's products.
    const basePlan = buildProductPlan(
      original as PensionAsset & { id?: number | string },
      p.expectedValue,
      ownerRetirementAge,
      ownerGender,
      ownerMarried,
      undefined,
      ownerInfo,
    );

    const override = earlyWithdrawalOverrides[String(basePlan.productId)];
    const usesEarlyKitzba =
      !belongsToPartner && isEarlyRetirement && override != null && isKitzbaCapable(basePlan.withdrawalType);

    let plan = basePlan;

    if (usesEarlyKitzba && override) {
      // Projected value at the (floored) start age for this product only.
      const startAge = Math.max(60, override.withdrawAtAge);
      const yearsToStart = Math.max(0, startAge - currentAge);
      const startValueResult = calculateExpectedPensionAtRetirement(
        [original as PensionAsset & { id?: number | string }],
        yearsToStart,
        fiatData
      );
      const projectedValueAtStart =
        startValueResult.includedProducts[0]?.expectedValue ?? p.expectedValue;

      plan = buildProductPlan(
        original as PensionAsset & { id?: number | string },
        p.expectedValue,
        ownerRetirementAge,
        ownerGender,
        ownerMarried,
        { startAge, projectedValueAtStart, officialAge: officialRetirementAge },
        ownerInfo,
      );

      if (plan.earlyKitzbaMonthlyPension && plan.earlyKitzbaMonthlyPension > 0) {
        const kitzbaFraction =
          plan.withdrawalType === PensionWithdrawalType.MIXED ? 0.7 : 1;
        pensionEarlyKitzbaStreams.push({
          productId: plan.productId,
          startAge: plan.earlyKitzbaStartAge ?? startAge,
          monthlyPension: plan.earlyKitzbaMonthlyPension,
          projectedValueAtStart: plan.earlyKitzbaProjectedValue ?? projectedValueAtStart,
          fractionOfProduct: kitzbaFraction,
        });
        handledEarlySellEventIds.push(override.eventId);
      }
    }

    perProductPlan.push(plan);
    // Exclude the early-kitzba portion from the eligibility-age aggregate cashflow
    // so it isn't double-counted (it starts earlier via the stream instead).
    if (!usesEarlyKitzba) {
      totalMonthlyPensionAtRetirement += plan.monthlyPensionAtRetirement;
      totalLumpSumAtRetirement += plan.lumpSumAtRetirement;
    } else {
      // MIXED products still pay their 30% hon lump sum at eligibility age.
      totalLumpSumAtRetirement += plan.lumpSumAtRetirement;
    }
    totalLumpSumTax += plan.lumpSumTax;
    if (belongsToPartner) {
      partnerMonthlyPensionContribution += plan.monthlyContribution;
      partnerExpectedPension += plan.projectedValueAtRetirement;
      partnerCurrentPensionValue += p.currentValue;
      if (!usesEarlyKitzba) {
        partnerMonthlyPensionAtRetirement += plan.monthlyPensionAtRetirement;
        partnerLumpSumAtRetirement += plan.lumpSumAtRetirement;
      } else {
        partnerLumpSumAtRetirement += plan.lumpSumAtRetirement;
      }
    } else {
      primaryMonthlyPensionContribution += plan.monthlyContribution;
    }
  }

  const currentPensionValue =
    inputs.startPortfolioByCategory?.[AssetCategory.PENSION] ?? 0;
  const monthlyIncome = inputs.monthlyIncome ?? 0;
  const partnerIncome = partnerInput?.monthlyIncome ?? 0;

  const pensionBreakdown: PensionBreakdown = {
    currentPensionValue,
    expectedPensionAtRetirement: expectedPensionResult.totalExpectedPension,
    yearsToPensionEligibility,
    pensionEligibilityAge,
    products: expectedPensionResult.includedProducts.map((p) => {
      const original =
        p.id != null
          ? pensionProducts.find((pp) => (pp as any).id === p.id)
          : pensionProducts.find((pp) => pp.name === p.name);
      const belongsToPartner =
        hasPartner && partnerProductKeys.has(String(p.id ?? p.name));
      return {
        id: p.id,
        name: p.name,
        currentValue: p.currentValue,
        expectedValue: p.expectedValue,
        productType: p.productType,
        monthlyContribution:
          (original?.monthlyDepositEmployee || 0) + (original?.monthlyDepositEmployer || 0),
        ownerId: belongsToPartner ? partnerInput?.ownerId : inputs.primaryOwnerId,
        ownerName: belongsToPartner ? partnerInput?.name : inputs.primaryName,
        belongsToPartner,
      };
    }),
    excludedProducts: expectedPensionResult.excludedProducts,
    perProductPlan,
    totalMonthlyPensionAtRetirement,
    totalLumpSumAtRetirement,
    totalLumpSumTax,
  };

  const primaryCashflow = totalMonthlyPensionAtRetirement - partnerMonthlyPensionAtRetirement;
  const primaryLumpSum = totalLumpSumAtRetirement - partnerLumpSumAtRetirement;
  const primaryExpectedPension =
    expectedPensionResult.totalExpectedPension - partnerExpectedPension;

  return {
    perProductPensionPlan: perProductPlan,
    pensionBreakdown,
    expectedPensionAtRetirement: primaryExpectedPension > 0 ? primaryExpectedPension : 0,
    pensionCashflowAtRetirement: primaryCashflow > 0 ? primaryCashflow : 0,
    pensionLumpSumAtRetirement: primaryLumpSum > 0 ? primaryLumpSum : 0,
    totalMonthlyPensionContribution: primaryMonthlyPensionContribution,
    pensionContributionRate:
      primaryMonthlyPensionContribution > 0 && monthlyIncome > 0
        ? primaryMonthlyPensionContribution / monthlyIncome
        : undefined,
    pensionEarlyKitzbaStreams,
    handledEarlySellEventIds,
    partner: hasPartner
      ? {
          expectedPensionAtRetirement: partnerExpectedPension > 0 ? partnerExpectedPension : undefined,
          pensionCashflowAtRetirement:
            partnerMonthlyPensionAtRetirement > 0 ? partnerMonthlyPensionAtRetirement : undefined,
          pensionLumpSumAtRetirement:
            partnerLumpSumAtRetirement > 0 ? partnerLumpSumAtRetirement : undefined,
          totalMonthlyPensionContribution:
            partnerMonthlyPensionContribution > 0 ? partnerMonthlyPensionContribution : undefined,
          pensionContributionRate:
            partnerMonthlyPensionContribution > 0 && partnerIncome > 0
              ? partnerMonthlyPensionContribution / partnerIncome
              : undefined,
          currentPensionValue: partnerCurrentPensionValue > 0 ? partnerCurrentPensionValue : undefined,
          pensionProductIds: partnerInput?.pensionProductIds,
        }
      : undefined,
  };
}
